// src/pages/settings/create-organization/OnboardingTreeView.tsx
import React, { useLayoutEffect, useRef } from 'react';
import { MessageCircle, Mic, Phone, Video } from 'lucide-react';
import '../../misc/onboarding/builder.css';
import { BubbleRow, ObNode } from '../../misc/onboarding/treeBits';
import type { LinkMap, StructureDraft } from '../../misc/onboarding/structureDraft';
import type { OrgAddons } from './orgSetup';

const UNIT = 124;

function spread(map: LinkMap, children: number, parents: number): LinkMap {
  if (Object.keys(map).length > 0) return map;
  const next: LinkMap = {};
  if (parents < 1 || children < 1) return next;
  const per = Math.ceil(children / parents);
  for (let i = 0; i < children; i += 1) next[i] = Math.min(parents - 1, Math.floor(i / per));
  return next;
}

type Badge = 'phone' | 'voice' | 'api' | 'wa' | 'video';

const BADGE_ICON = { phone: Phone, voice: Mic, api: MessageCircle, wa: MessageCircle, video: Video } as const;

function advisorBadges(index: number, addons: OrgAddons): Badge[] {
  const list: Badge[] = [];
  if (addons.phone && index < addons.phoneQty) list.push('phone');
  if (addons.phone && ((addons.voice && index < addons.voiceQty) || (addons.voicePro && index < addons.voiceProQty))) list.push('voice');
  if (addons.waSeller && index < addons.waSellerQty) list.push('api');
  list.push('wa');
  if (addons.videoIncluded && addons.videoScope === 'person' && index < addons.videoQty) list.push('video');
  return list;
}

function teamBadges(index: number, addons: OrgAddons): Badge[] {
  const list: Badge[] = [];
  if (addons.waTeam && index < addons.waTeamQty) list.push('api');
  if (addons.videoIncluded && addons.videoScope === 'team' && index < addons.videoQty) list.push('video');
  return list;
}

function orgBadges(addons: OrgAddons): Badge[] {
  const list: Badge[] = [];
  if (addons.waCompany) list.push('api');
  if (addons.videoIncluded && addons.videoScope === 'company') list.push('video');
  if (addons.videoOwn) list.push('video');
  return list;
}

const BadgeRow = ({ kinds }: { kinds: Badge[] }) => (
  kinds.length ? (
    <span className="ob-badges">
      {kinds.map((kind) => {
        const Icon = BADGE_ICON[kind];
        return (
          <span key={kind} className={`org-badge${kind === 'wa' ? ' green' : ''}`} aria-hidden>
            <Icon size={12} strokeWidth={2.4} />
          </span>
        );
      })}
    </span>
  ) : null
);

/** The same onboarding tree. Summary is the last step. Sales is the teams-and-advisors step. */
const OnboardingTreeView: React.FC<{
  draft: StructureDraft;
  mode: 'summary' | 'sales';
  addons?: OrgAddons;
  /** Fill the onboarding stage and scale the tree the way the last public step does. */
  fit?: boolean;
}> = ({ draft, mode, addons, fit }) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const linesRef = useRef<SVGSVGElement>(null);
  const orgLabel = draft.name || (draft.path === 3 ? 'Tu organización' : 'Tu empresa');
  const cols = mode === 'summary' ? 4 : 3;

  useLayoutEffect(() => {
    const stage = stageRef.current;
    const body = bodyRef.current;
    const svg = linesRef.current;
    if (!stage || !body || !svg) return;

    const draw = () => {
      body.style.transform = 'none';
      if (fit) {
        const h = stage.clientHeight;
        if (h < 40) return;
        const sc = Math.min(0.62, (h - 24) / Math.max(body.offsetHeight, 1));
        const y = Math.max(8, (h - body.offsetHeight * sc) / 2);
        body.style.transformOrigin = 'top center';
        body.style.transform = `translateY(${y}px) scale(${sc})`;
      } else {
        const needed = Math.max(body.scrollWidth, body.offsetWidth);
        const sc = Math.min(1, (stage.clientWidth - 8) / Math.max(needed, 1));
        body.style.transformOrigin = 'top center';
        body.style.transform = `scale(${sc})`;
        const nextH = `${Math.ceil(body.offsetHeight * sc + 8)}px`;
        if (stage.style.height !== nextH) stage.style.height = nextH;
      }

      svg.innerHTML = '';
      const w = Math.max(body.offsetWidth, 1);
      const h = Math.max(body.offsetHeight, 1);
      svg.setAttribute('width', String(w));
      svg.setAttribute('height', String(h));
      svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
      const ns = 'http://www.w3.org/2000/svg';
      const origin = body.getBoundingClientRect();
      const sx = origin.width / w;
      const sy = origin.height / h;
      const pt = (el: Element, side: 't' | 'b') => {
        const target = el.classList.contains('ob-blabel') ? el : (el.querySelector('.ob-chip') || el);
        const r = target.getBoundingClientRect();
        return {
          x: (r.left + r.width / 2 - origin.left) / sx,
          y: ((side === 'b' ? r.bottom : r.top) - origin.top) / sy,
        };
      };
      const seg = (x1: number, y1: number, x2: number, y2: number) => {
        const p = document.createElementNS(ns, 'path');
        const my = (y1 + y2) / 2;
        p.setAttribute('d', `M${x1} ${y1} C${x1} ${my} ${x2} ${my} ${x2} ${y2}`);
        p.setAttribute('fill', 'none');
        p.setAttribute('stroke', 'rgba(255,255,255,.55)');
        p.setAttribute('stroke-width', '1.6');
        svg.appendChild(p);
      };
      const nodes = (root: ParentNode | null) => (root ? Array.from(root.querySelectorAll('.ob-node')) : []);
      const linkKids = (parents: Element[], children: Element[], map: LinkMap, fill = false) => {
        const links = fill ? spread(map, children.length, parents.length) : map;
        children.forEach((child, i) => {
          const pi = links[i];
          const parent = pi == null ? null : parents[pi];
          if (!parent) return;
          const a = pt(parent, 'b');
          const c = pt(child, 't');
          seg(a.x, a.y, c.x, c.y);
        });
      };

      if (mode === 'summary') {
        const org = body.querySelector('.ob-node.org');
        if (!org) return;
        const ob = pt(org, 'b');
        body.querySelectorAll('.ob-blabel').forEach((label) => {
          const top = pt(label, 't');
          seg(ob.x, ob.y, top.x, top.y);
        });
        const emp = body.querySelector('[data-sum="emp"]');
        const pro = body.querySelector('[data-sum="pro"]');
        if (draft.path === 3 && emp && pro) linkKids(nodes(emp), nodes(pro), draft.links);
        const eq = body.querySelector('[data-sum="eq"]');
        const ase = body.querySelector('[data-sum="ase"]');
        if (eq && ase) linkKids(nodes(eq), nodes(ase), draft.assign);
        return;
      }

      const org = body.querySelector('.ob-node.org');
      if (!org) return;
      const o = pt(org, 'b');
      const eq = body.querySelector('[data-row="eq"]');
      const ase = body.querySelector('[data-row="ase"]');
      nodes(eq).forEach((n) => {
        const c = pt(n, 't');
        seg(o.x, o.y, c.x, c.y);
      });
      if (eq && ase) linkKids(nodes(eq), nodes(ase), draft.assign, true);
    };

    draw();
    const frame = window.requestAnimationFrame(draw);
    const ro = new ResizeObserver(draw);
    ro.observe(stage);
    return () => {
      window.cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [draft, mode, addons, cols, fit]);

  const adminCount = Math.max(draft.admins, 0);
  const adminShown = Math.min(adminCount, 6);
  const adminExtra = adminCount - adminShown;

  return (
    <div className={`org-ob${mode === 'sales' ? ' sales' : ''}${fit ? ' fit' : ''}`} ref={stageRef}>
      {mode === 'summary' ? (
        <div className="ob-sum" ref={bodyRef}>
          <svg className="ob-lines" ref={linesRef} />
          <div className="ob-row"><ObNode kind="org" name={orgLabel} org empty={!draft.name} /></div>
          <div className="ob-branches">
            <div className="ob-branch">
              <span className="ob-blabel">{draft.path === 3 ? 'Tu estructura' : 'Tu empresa'}</span>
              {draft.path === 3 && (
                <div data-sum="emp"><BubbleRow unit={UNIT} kind="empresa" count={draft.empresas} label="Empresa" cols={cols} rows={2} /></div>
              )}
              <div data-sum="pro"><BubbleRow unit={UNIT} kind="proyecto" count={draft.proyectos} label="Proyecto" cols={cols} rows={3} /></div>
            </div>
            <div className="ob-branch">
              <span className="ob-blabel">Tus ventas</span>
              <div data-sum="eq"><BubbleRow unit={UNIT} kind="equipo" count={draft.equipos} label="Equipo" cols={cols} rows={2} /></div>
              <div data-sum="ase"><BubbleRow unit={UNIT} kind="asesor" count={draft.asesores} label="Asesor" cols={cols} rows={3} /></div>
            </div>
          </div>
          <BubbleRow unit={UNIT} kind="admin" count={draft.admins} label="Admin" cols={cols * 2 + 1} rows={1} />
        </div>
      ) : (
        <div className="ob-trees" ref={bodyRef}>
          <svg className="ob-lines" ref={linesRef} />
          <div className="ob-tree">
            <div className="ob-row">
              <ObNode kind="org" name={orgLabel} org empty={!draft.name} adorn={addons ? <BadgeRow kinds={orgBadges(addons)} /> : undefined} />
            </div>
            <div data-row="eq">
              <BubbleRow
                unit={UNIT}
                kind="equipo"
                count={Math.max(draft.equipos, 1)}
                label="Equipo"
                cols={cols}
                rows={2}
                adorn={addons ? (i) => <BadgeRow kinds={teamBadges(i, addons)} /> : undefined}
              />
            </div>
            <div data-row="ase">
              <BubbleRow
                unit={UNIT}
                kind="asesor"
                count={draft.asesores}
                label="Asesor"
                cols={cols}
                rows={3}
                adorn={addons ? (i) => <BadgeRow kinds={advisorBadges(i, addons)} /> : undefined}
              />
            </div>
          </div>
          <div className="ob-admins" style={{ gridTemplateRows: `repeat(${Math.max(adminShown, 1)}, auto)` }}>
            {Array.from({ length: adminShown }, (_, i) => (
              <ObNode key={i} kind="admin" name={`Admin ${i + 1}`} />
            ))}
            {adminExtra > 0 ? <div className="ob-more">+{adminExtra}</div> : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingTreeView;
